import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, Save, Wand2, ArrowRight, Mic } from "lucide-react";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import VoiceRecorder from "../components/VoiceRecorder";
import CardAudio from "../components/CardAudio";
import BulkImport from "../components/BulkImport";

const LANGUAGES = ["Spanish", "French", "Japanese", "German", "Italian", "Portuguese", "Korean", "Mandarin", "Russian"];
const COLORS = ["#FFD166", "#06D6A0", "#EF476F", "#118AB2"];
const FLAGS_BY_LANG = {
  Spanish: "🇪🇸", French: "🇫🇷", Japanese: "🇯🇵", German: "🇩🇪",
  Italian: "🇮🇹", Portuguese: "🇵🇹", Korean: "🇰🇷", Mandarin: "🇨🇳", Russian: "🇷🇺",
};

export default function Create() {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("Spanish");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const newCard = () => ({ _key: crypto.randomUUID(), front: "", back: "", example: "" });
  const [cards, setCards] = useState([newCard()]);
  const [aiTopic, setAiTopic] = useState("");
  const [aiCount, setAiCount] = useState(10);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState(null); // {course, cards}

  const updateCard = (i, key, val) => {
    setCards((c) => c.map((card, idx) => idx === i ? { ...card, [key]: val } : card));
  };

  const addCard = () => setCards((c) => [...c, newCard()]);
  const removeCard = (i) => setCards((c) => c.filter((_, idx) => idx !== i));

  const runAI = async () => {
    if (!aiTopic.trim()) {
      toast.error("Enter a topic first");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/generate-deck", {
        topic: aiTopic,
        language,
        count: aiCount,
      });
      if (data.title && !title) setTitle(data.title);
      if (data.description && !description) setDescription(data.description);
      if (Array.isArray(data.cards) && data.cards.length > 0) {
        setCards(data.cards.map((c) => ({
          _key: crypto.randomUUID(),
          front: c.front || "",
          back: c.back || "",
          example: c.example || "",
        })));
        toast.success(`Generated ${data.cards.length} cards! Edit before saving.`);
      } else {
        toast.error("AI didn't return cards. Try a different topic.");
      }
    } catch {
      toast.error("AI generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    if (!title.trim()) return toast.error("Title required");
    const valid = cards.filter((c) => c.front.trim() && c.back.trim());
    if (valid.length < 2) return toast.error("Add at least 2 cards");
    setSaving(true);
    try {
      const { data } = await api.post("/courses", {
        title,
        language,
        description,
        level: "Beginner",
        flag: FLAGS_BY_LANG[language] || "📚",
        color,
        cards: valid,
      });
      toast.success("Deck published! Optionally record voice for each card.");
      setPublished(data);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (published) {
    return (
      <div className="min-h-screen grain-bg bg-brand-peach" data-testid="create-audio-step">
        <Nav />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
          <div className="mb-6">
            <div className="brutal-badge bg-brand-mint mb-3"><Mic className="w-3.5 h-3.5" strokeWidth={3} /> Optional</div>
            <h1 className="font-display text-4xl font-bold text-brand-navy">Add your voice</h1>
            <p className="text-brand-navy/70 mt-2 text-lg">
              Record a short pronunciation for each card — or skip and use AI audio automatically.
            </p>
          </div>

          <div className="space-y-3 mb-8">
            {(published.cards || []).map((c, i) => (
              <div key={c.id} className="brutal-card-sm p-4 bg-white">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-xs font-bold uppercase text-brand-navy/50">Card {i + 1}</div>
                    <div className="font-display font-bold text-lg text-brand-navy">{c.front}</div>
                    <div className="font-display font-bold text-brand-pink">{c.back}</div>
                  </div>
                  <CardAudio
                    cardId={c.id}
                    existingUrl={c.audio_url}
                    onUploaded={() => {}}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => nav(`/learn/${published.id}`)}
              data-testid="create-done-btn"
              className="brutal-btn-pink text-lg"
            >
              Start learning <ArrowRight className="w-5 h-5" strokeWidth={3} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain-bg bg-brand-peach" data-testid="create-page">
      <Nav />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-8">
          <div className="brutal-badge bg-brand-mint mb-3"><Plus className="w-3.5 h-3.5" strokeWidth={3} /> New deck</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-navy">Create your own deck</h1>
          <p className="text-brand-navy/70 mt-2 text-lg">Manual, AI-assisted, or a mix of both.</p>
        </div>

        {/* Meta */}
        <div className="brutal-card p-6 md:p-8 bg-white mb-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold uppercase text-brand-navy tracking-wide">Deck title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="create-title" className="brutal-input mt-1" placeholder="e.g. Café phrases" />
            </div>
            <div>
              <label className="text-sm font-bold uppercase text-brand-navy tracking-wide">Language</label>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="create-language" className="brutal-input mt-1">
                {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-bold uppercase text-brand-navy tracking-wide">Short description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="create-description" className="brutal-input mt-1" placeholder="What will learners get?" />
          </div>
          <div>
            <label className="text-sm font-bold uppercase text-brand-navy tracking-wide">Color</label>
            <div className="flex gap-2 mt-2">
              {COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)} data-testid={`create-color-${c}`} className={`w-10 h-10 rounded-full border-2 border-brand-ink shadow-brutal ${color === c ? "ring-4 ring-brand-yellow/60" : ""}`} style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
        </div>

        {/* Bulk import (paste / CSV / XLSX / Sheets) */}
        <BulkImport
          language={language}
          onImported={(rows) => {
            setCards((c) => {
              // Drop the initial empty-draft row if it's still blank
              const cleaned = c.filter((x) => x.front.trim() || x.back.trim());
              return [
                ...cleaned,
                ...rows.map((r) => ({ _key: crypto.randomUUID(), front: r.front, back: r.back, example: r.example || "" })),
              ];
            });
          }}
        />

        {/* AI generator */}
        <div className="brutal-card p-6 md:p-8 mb-6 bg-brand-yellow">
          <div className="flex items-center gap-3 mb-4">
            <Wand2 className="w-6 h-6 text-brand-navy" strokeWidth={2.5} />
            <h2 className="font-display text-2xl font-bold text-brand-navy">Or, let AI draft it</h2>
          </div>
          <p className="text-brand-navy/80 mb-4 text-sm">Type a topic — Lumi will produce a starter deck you can edit.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              data-testid="create-ai-topic"
              placeholder="e.g. Ordering coffee in Paris"
              className="brutal-input flex-1 bg-white"
            />
            <input
              type="number"
              min={4}
              max={20}
              value={aiCount}
              onChange={(e) => setAiCount(parseInt(e.target.value) || 10)}
              data-testid="create-ai-count"
              className="brutal-input w-24 bg-white"
            />
            <button onClick={runAI} disabled={aiLoading} data-testid="create-ai-btn" className="brutal-btn-pink">
              <Sparkles className="w-5 h-5" strokeWidth={3} />
              {aiLoading ? "Generating..." : "Generate"}
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="brutal-card p-6 md:p-8 bg-white mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-bold text-brand-navy">Cards ({cards.length})</h2>
            <button onClick={addCard} data-testid="create-add-card" className="brutal-btn-mint text-sm py-2">
              <Plus className="w-4 h-4" strokeWidth={3} /> Add card
            </button>
          </div>
          <div className="space-y-3">
            {cards.map((c, i) => (
              <div key={c._key || i} className="brutal-card-sm p-4 bg-brand-cream">
                <div className="flex items-center justify-between mb-3">
                  <div className="brutal-badge bg-white text-xs">Card {i + 1}</div>
                  <button onClick={() => removeCard(i)} data-testid={`create-remove-card-${i}`} className="text-brand-pink hover:text-brand-navy">
                    <Trash2 className="w-4 h-4" strokeWidth={3} />
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <input value={c.front} onChange={(e) => updateCard(i, "front", e.target.value)} data-testid={`create-card-front-${i}`} placeholder={`${language} (front)`} className="brutal-input" />
                  <input value={c.back} onChange={(e) => updateCard(i, "back", e.target.value)} data-testid={`create-card-back-${i}`} placeholder="English (back)" className="brutal-input" />
                </div>
                <input value={c.example} onChange={(e) => updateCard(i, "example", e.target.value)} data-testid={`create-card-example-${i}`} placeholder={`Optional example sentence in ${language}`} className="brutal-input mt-3" />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} data-testid="create-save-btn" className="brutal-btn-pink text-lg">
            <Save className="w-5 h-5" strokeWidth={3} /> {saving ? "Publishing..." : "Publish deck"}
          </button>
        </div>
      </div>
    </div>
  );
}
